// @givmo/social — Posts: the organization's history, newest first (SMM-WEB1; §SMM-8 point 2,
// "History").
//
// Server-paginated, a page at a time (Previous / Next: no infinite scroll), filtered by a state and,
// from the Email Campaign entry, by its campaign (the context pair, carried in the address). Each
// post shows its words, its image and its time, then every account's post in its own state. A
// Member who only reads sees the same page with no acts on it.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { sdk, type SocialPostOut } from '@givmo/api-client';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorMessage,
  FilterBar,
  FilterChip,
  Icon,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { canPublish, type SocialConfig } from './social-config';
import {
  EditScheduledDialog,
  VariantRow,
  actionErrorWords,
  editableWhenScheduled,
  postWhen,
  useCancelPost,
} from './post-parts';
import { useAttentionCount, usePosts, useRefreshSocial, type PostsFilter } from './social-queries';
import { SOCIAL_PAGE_TEST_ID, SOCIAL_ROUTES, socialPath } from './social-routes';
import { SocialLoading, SocialPageShell, SocialRefused } from './social-ui';
import { socialWords as w } from './social-words';

/** The history's state filter: a variant status the read filters on, or every post. */
const FILTERS: readonly { id: string; status: string | null; label: string }[] = [
  { id: 'all', status: null, label: w.filterAll },
  { id: 'draft', status: 'draft', label: w.filterDrafts },
  { id: 'queued', status: 'queued', label: w.filterScheduled },
  { id: 'published', status: 'published', label: w.filterPublished },
  { id: 'failed', status: 'failed', label: w.filterFailed },
];

/** The campaign a history is filtered to, from the address (`?context_kind=…&context_ref=…`). */
export interface PostsContextFilter {
  kind: string;
  ref: string;
  label: string | null;
}

export function readContextFilter(search: string): PostsContextFilter | null {
  const params = new URLSearchParams(search);
  const kind = params.get('context_kind');
  const ref = params.get('context_ref');
  if (kind !== 'fundraiser_campaign' || !ref || !/^[0-9a-f-]{36}$/i.test(ref)) return null;
  const label = params.get('context_label');
  return { kind, ref, label: label && label.trim().length > 0 ? label.trim() : null };
}

export function PostsPage({
  config,
  context: initialContext = null,
}: {
  config: SocialConfig;
  context?: PostsContextFilter | null;
}) {
  const { orgRef } = config;
  const publisher = canPublish(config);
  const navigate = useNavigate();
  const [filterId, setFilterId] = useState('all');
  const [offset, setOffset] = useState(0);
  const [context, setContext] = useState<PostsContextFilter | null>(initialContext);
  const filter: PostsFilter = {
    status: FILTERS.find((entry) => entry.id === filterId)?.status ?? null,
    contextKind: context?.kind ?? null,
    contextRef: context?.ref ?? null,
  };
  const page = usePosts(orgRef, filter, offset);
  const attention = useAttentionCount(orgRef);
  const pageSize = 20;
  const needing = attention.data?.count ?? 0;

  return (
    <SocialPageShell
      title={w.postsTitle}
      description={w.postsLede}
      testId={SOCIAL_PAGE_TEST_ID}
      actions={
        publisher ? (
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
            onClick={() => void navigate({ to: SOCIAL_ROUTES.compose })}
            testId="social-new-post"
          >
            {w.newPost}
          </Button>
        ) : undefined
      }
    >
      {needing > 0 && filterId !== 'failed' ? (
        <div
          className="flex flex-wrap items-center gap-8 rounded-md border border-warning-100 bg-warning-50 px-16 py-10"
          {...testIdProp('social-attention')}
        >
          <span className="font-sans text-s14 font-w500 tracking-givmo text-warning-700">
            {w.attentionLine(needing, attention.data?.capped ?? false)}
          </span>
          <Button
            appearance="saas"
            tone="link"
            size="sm"
            onClick={() => {
              setFilterId('failed');
              setOffset(0);
            }}
            testId="social-attention-show"
          >
            {w.showThem}
          </Button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-12">
        <FilterBar ariaLabel={w.filterLabel} testId="social-filters">
          {FILTERS.map((entry) => (
            <FilterChip
              key={entry.id}
              label={entry.label}
              selected={filterId === entry.id}
              onToggle={() => {
                setFilterId(entry.id);
                setOffset(0);
              }}
              testId={`social-filter-${entry.id}`}
            />
          ))}
        </FilterBar>
        {context ? (
          <span
            className="flex items-center gap-8 font-sans text-s13 tracking-givmo text-text-secondary"
            {...testIdProp('social-context-filter')}
          >
            {w.campaignFilter(context.label ?? w.campaignUnnamed)}
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              onClick={() => {
                setContext(null);
                setOffset(0);
              }}
              testId="social-context-filter-clear"
            >
              {w.clearCampaignFilter}
            </Button>
          </span>
        ) : null}
      </div>
      {orgRef === null || page.isPending ? (
        <SocialLoading>{w.loadingPosts}</SocialLoading>
      ) : page.isError ? (
        <SocialRefused error={page.error} onRetry={() => void page.refetch()} />
      ) : page.data.posts.length === 0 && offset === 0 ? (
        <Card className="px-24 py-32" testId="social-posts-empty">
          <EmptyState
            title={filter.status || context ? w.noFilteredPosts : w.noPostsTitle}
            description={filter.status || context ? undefined : w.noPostsBody}
          />
        </Card>
      ) : (
        <>
          <ul className="flex flex-col gap-12" {...testIdProp('social-posts')}>
            {page.data.posts.map((post) => (
              <PostCard key={post.post_id} orgRef={orgRef} post={post} canAct={publisher} />
            ))}
          </ul>
          {offset > 0 || page.data.hasMore ? (
            <div className="flex items-center justify-end gap-8" {...testIdProp('social-pages')}>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - pageSize))}
                testId="social-page-previous"
              >
                {w.previous}
              </Button>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={!page.data.hasMore}
                onClick={() => setOffset(offset + pageSize)}
                testId="social-page-next"
              >
                {w.next}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </SocialPageShell>
  );
}

function PostCard({
  orgRef,
  post,
  canAct,
}: {
  orgRef: string;
  post: SocialPostOut;
  canAct: boolean;
}) {
  const cancel = useCancelPost(orgRef, post);
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [editingScheduled, setEditingScheduled] = useState(false);
  const isDraft = post.editable;
  const canCancel = post.variants.some((variant) => variant.can_cancel);
  const canEditScheduled = !isDraft && editableWhenScheduled(post);
  const id = `social-post-${post.post_id}`;
  return (
    <li>
      <Card className="flex flex-col gap-12 px-20 py-16" testId={id}>
        <div className="flex items-start gap-14">
          {post.image ? (
            <img
              src={post.image.url}
              alt=""
              className="size-[64px] shrink-0 rounded-md border border-border-subtle object-cover"
              {...testIdProp(`${id}-image`)}
            />
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <p
              className="line-clamp-3 whitespace-pre-line font-sans text-s14 tracking-givmo text-text-strong"
              {...testIdProp(`${id}-text`)}
            >
              {post.text}
            </p>
            <p className="font-sans text-s12 tracking-givmo text-text-secondary">
              {isDraft
                ? w.draftBy(post.author_name ?? '')
                : `${postWhen(post)}${post.author_name ? ` · ${w.by(post.author_name)}` : ''}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-8">
            {canAct && isDraft ? (
              <>
                {post.deletable ? (
                  <Button
                    appearance="saas"
                    tone="ghost"
                    size="sm"
                    onClick={() => setDeleting(true)}
                    testId={`${id}-delete`}
                  >
                    {w.deleteConfirm}
                  </Button>
                ) : null}
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() =>
                    void navigate({ to: socialPath({ page: 'post', postId: post.post_id }) })
                  }
                  testId={`${id}-edit`}
                >
                  {w.edit}
                </Button>
              </>
            ) : null}
            {canAct && canCancel ? (
              <Button
                appearance="saas"
                tone="ghost"
                size="sm"
                loading={cancel.isPending}
                onClick={() => cancel.mutate()}
                testId={`${id}-cancel`}
              >
                {w.cancelScheduled}
              </Button>
            ) : null}
            {canAct && canEditScheduled ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => setEditingScheduled(true)}
                testId={`${id}-edit-scheduled`}
              >
                {w.editScheduled}
              </Button>
            ) : null}
          </div>
        </div>
        {cancel.isError ? (
          <ErrorMessage testId={`${id}-cancel-error`}>
            {actionErrorWords(cancel.error)}
          </ErrorMessage>
        ) : null}
        {!isDraft ? (
          <ul className="flex flex-col border-t border-border-subtle pt-4">
            {post.variants.map((variant) => (
              <VariantRow
                key={variant.variant_id}
                orgRef={orgRef}
                variant={variant}
                canAct={canAct}
              />
            ))}
          </ul>
        ) : null}
      </Card>
      {editingScheduled ? (
        <EditScheduledDialog
          orgRef={orgRef}
          post={post}
          onClose={() => setEditingScheduled(false)}
        />
      ) : null}
      {deleting ? (
        <DeleteDraftDialog
          orgRef={orgRef}
          postId={post.post_id}
          onClose={() => setDeleting(false)}
        />
      ) : null}
    </li>
  );
}

export function DeleteDraftDialog({
  orgRef,
  postId,
  onClose,
  onDeleted,
}: {
  orgRef: string;
  postId: string;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const refresh = useRefreshSocial(orgRef);
  const remove = useMutation({
    mutationFn: async () => {
      await sdk.orgSocialDeleteOrgSocialPost({
        path: { org_ref: orgRef, post_id: postId },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.draftDeleted);
      await refresh();
      onClose();
      onDeleted?.();
    },
  });
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={w.deleteDraftTitle}
        width={420}
        className="flex flex-col gap-16"
        testId="social-delete-draft-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
          {w.deleteDraftTitle}
        </Dialog.Title>
        <p className="font-sans text-s14 tracking-givmo text-text-secondary">{w.deleteDraftBody}</p>
        {remove.isError ? (
          <ErrorMessage testId="social-delete-draft-error">
            {actionErrorWords(remove.error)}
          </ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-8">
          <Button appearance="saas" tone="secondary" onClick={onClose}>
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
            testId="social-delete-draft-confirm"
          >
            {w.deleteConfirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
